/**
 * Small read-only badge on a Study Documents card showing the check that ran
 * when the document was generated. Clicking it opens the Verification page.
 * No stored check on the document (generated before the check existed) means
 * no badge. This is a different check from ProseDocChip's cross-document line.
 */
import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import {
  generationCheckBadge,
  type DismissalLike,
  type StoredGenerationDoc,
} from '../../features/verification/generationCheck';

const CLASSES: Record<string, string> = {
  checked: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
  findings: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
  dismissed: 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200',
  notChecked: 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200',
};

export default function GenerationCheckBadge({ projectId, docId }: { projectId: string; docId: string }) {
  const navigate = useNavigate();
  const [stored, setStored] = useState<StoredGenerationDoc>(null);

  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      doc(db, 'projects', projectId, 'dataManagement', docId),
      (snap) => setStored(snap.exists() ? (snap.data() as StoredGenerationDoc) : null),
      () => setStored(null),
    );
    return () => unsub();
  }, [projectId, docId]);

  const [dismissals, setDismissals] = useState<DismissalLike[]>([]);
  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      collection(db, 'projects', projectId, 'validationDismissals'),
      (snap) => {
        const out: DismissalLike[] = [];
        snap.forEach((d) => {
          const x = d.data() as { key?: string; fingerprint?: string };
          if (x?.key) out.push({ key: x.key, fingerprint: x.fingerprint ?? '' });
        });
        setDismissals(out);
      },
      () => setDismissals([]),
    );
    return () => unsub();
  }, [projectId]);

  const badge = generationCheckBadge(stored, { docId, dismissals });
  if (!badge) return null;
  return (
    <button
      type="button"
      onClick={() => navigate(`/project/${projectId}/validation`)}
      title={badge.title}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors mt-1 ${CLASSES[badge.state]}`}
    >
      {badge.label}
    </button>
  );
}
